import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

/// Shared JoFotara triage widgets — used by:
///   - `admin/src/pages/PosSales.tsx` (in-store walk-in sales)
///   - `admin/src/pages/Orders.tsx` (delivery orders — customer-app +
///     phone-in). Delivery orders started submitting to JoFotara on the
///     `status='delivered'` transition as of 2026-07-10 (see
///     `docs/operations/jofotara.md`), so the drawer needs the same
///     status chip + retry + raw-response viewer the POS drawer has.
///
/// Both surfaces render `JofotaraTriageCard` in the detail drawer and
/// (optionally) `JofotaraRowChip` in the table row.

export type JofotaraStatus =
  | 'accepted'
  | 'submitted'
  | 'pending'
  | 'retrying'
  | 'rejected'
  | 'skipped'
  | null

// Chip colour classes keyed by status. Accepted = green (good),
// retrying/pending = amber (in-flight), rejected = red (admin fix),
// skipped = grey (master switch was off, bulk-submittable).
export const JOFOTARA_CHIP_CLASSES: Record<string, string> = {
  accepted: 'bg-green-500/15 text-green-400',
  submitted: 'bg-blue-500/15 text-blue-300',
  pending: 'bg-amber-500/15 text-amber-300',
  retrying: 'bg-amber-500/15 text-amber-300',
  rejected: 'bg-red-500/15 text-red-400',
  skipped: 'bg-gray-500/15 text-gray-400',
}

export const JOFOTARA_CHIP_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  submitted: 'Submitted',
  pending: 'Pending',
  retrying: 'Retrying',
  rejected: 'Rejected',
  skipped: 'Skipped',
}

interface RowChipProps {
  saleStatus: JofotaraStatus
  voidStatus?: JofotaraStatus
}

/// Compact chip for a table row. Surfaces the sale status; if a void
/// credit-note submission also exists and is non-accepted, a second
/// smaller chip flags that separately.
export function JofotaraRowChip({ saleStatus, voidStatus }: RowChipProps) {
  const s = saleStatus ?? '—'
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-block w-fit text-[10px] px-1.5 py-0.5 rounded font-medium ${JOFOTARA_CHIP_CLASSES[s] ?? 'bg-gray-700/30 text-gray-500'}`}>
        {JOFOTARA_CHIP_LABELS[s] ?? s}
      </span>
      {voidStatus && voidStatus !== 'accepted' && (
        <span
          className={`inline-block w-fit text-[9px] px-1.5 py-0.5 rounded font-medium ${JOFOTARA_CHIP_CLASSES[voidStatus] ?? 'bg-gray-700/30 text-gray-500'}`}
          title="Void credit-note status (when non-accepted)"
        >
          Void: {JOFOTARA_CHIP_LABELS[voidStatus] ?? voidStatus}
        </span>
      )}
    </div>
  )
}

interface TriageCardProps {
  kind: 'sale' | 'void'
  orderId: string
  status: JofotaraStatus
  invoiceId: string | null
  uuid: string | null
  submittedAt: string | null
  attempts: number | null
  responseJson: string | null
  /// React-query keys to invalidate after a successful retry. Both
  /// consumers pass their page-level list key + their detail key so
  /// the UI reflects the reset status without a page reload.
  invalidateKeys?: (string | number)[][]
}

/// Detail-drawer card for a single JoFotara submission (sale OR void).
/// Shows invoice id + EINV_UUID + status. When non-accepted, an
/// expandable ISTD raw-response viewer surfaces the exact rejection
/// (so admin doesn't have to dig into wrangler tail / D1). Retry button
/// hits `POST /admin/orders/:id/jofotara-retry` with the right `kind`
/// — the endpoint zeroes attempts + flips status to `'retrying'` so
/// the minute-cron picks it up on the next pass.
export function JofotaraTriageCard({
  kind, orderId, status, invoiceId, uuid, submittedAt, attempts, responseJson, invalidateKeys,
}: TriageCardProps) {
  const qc = useQueryClient()
  const [showRaw, setShowRaw] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)

  const retry = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/jofotara-retry`, { kind }).then(r => r.data),
    onSuccess: () => {
      (invalidateKeys ?? []).forEach((k) => qc.invalidateQueries({ queryKey: k }))
    },
    onError: (e: unknown) => setRetryError(e instanceof Error ? e.message : String(e)),
  })

  if (!status) return null
  const isRetryable = status === 'rejected' || status === 'retrying' || status === 'skipped'
  const isAccepted = status === 'accepted'
  const showResponseViewer = !isAccepted && !!responseJson

  return (
    <div className="rounded-lg border border-[#2e2e2e] bg-[#161616] p-3 text-xs space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
            JoFotara {kind === 'void' ? 'credit-note (void)' : 'invoice (sale)'}
          </p>
          <p className="text-white font-mono mt-0.5">{invoiceId ?? '—'}</p>
        </div>
        <span className={`inline-block text-[10px] px-2 py-0.5 rounded font-medium ${JOFOTARA_CHIP_CLASSES[status] ?? 'bg-gray-700/30 text-gray-500'}`}>
          {JOFOTARA_CHIP_LABELS[status] ?? status}
        </span>
      </div>
      <div className="text-gray-500 grid grid-cols-2 gap-x-3 gap-y-0.5">
        {uuid && <div><span className="text-gray-600">EINV UUID:</span> <span className="font-mono text-gray-400 break-all">{uuid}</span></div>}
        {submittedAt && <div><span className="text-gray-600">Submitted:</span> <span className="text-gray-400">{formatDateTime(submittedAt)}</span></div>}
        {attempts != null && <div><span className="text-gray-600">Attempts:</span> <span className="text-gray-400">{attempts}</span></div>}
      </div>
      {showResponseViewer && (
        <div>
          <button
            type="button"
            onClick={() => setShowRaw(s => !s)}
            className="text-[11px] text-gray-400 hover:text-gray-200 underline"
          >
            {showRaw ? 'Hide ISTD response' : 'Show raw ISTD response'}
          </button>
          {showRaw && (
            <pre className="mt-2 max-h-48 overflow-auto rounded bg-black/40 border border-[#2e2e2e] p-2 text-[10px] text-gray-300 whitespace-pre-wrap break-all">
              {prettyJson(responseJson)}
            </pre>
          )}
        </div>
      )}
      {isRetryable && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setRetryError(null); retry.mutate() }}
            disabled={retry.isPending}
            className="px-2.5 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
          >
            {retry.isPending ? 'Resetting…' : 'Retry submission'}
          </button>
          {retry.isSuccess && <span className="text-[11px] text-green-400">Queued for cron retry</span>}
          {retryError && <span className="text-[11px] text-red-400">{retryError}</span>}
        </div>
      )}
    </div>
  )
}

function prettyJson(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw }
}
